import { Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { PagedResponse, SessionService, User } from '@edl-development/edl-common';
import { ToastrService } from 'ngx-toastr';
import { finalize } from 'rxjs/operators';
import { CplNote } from 'src/app/model/cpl-note.model';
import { AccessRoleHelper } from 'src/app/model/helpers/access-role.helper';
import { NoteService } from 'src/app/services/note.service';

const FIRST_PAGE = 1;

@Component({
    selector: 'edl-notes',
    templateUrl: './edl-notes.component.html',
    styleUrls: ['./edl-notes.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class EdllNotesComponent implements OnInit {
    @Input() typeOfNote: number;
    @Input() typeId: string;
    @Input() notesToInclude: number;
    @Input() isReadOnly: boolean = false;

    @Output() onSubmissionComplete = new EventEmitter<boolean>();

    addNoteForm: UntypedFormGroup;
    notes: PagedResponse;
    busy: boolean;
    currentPage: number;
    currentNote: CplNote;
    user: User;

    constructor(
        private noteService: NoteService,
        private sessionService: SessionService,
        private formBuilder: UntypedFormBuilder,
        private toastr: ToastrService
    ) {}

    ngOnInit() {
        this.busy = false;
        this.user = this.sessionService.user;
        this.addNoteForm = this.generateDefaultNoteForm();

        this.refreshNotesList(FIRST_PAGE);
    }

    editNote(note: CplNote) {
        this.currentNote = note;

        this.addNoteForm = this.formBuilder.group({
            NoteID: [note.NoteID, []],
            UserID: [note.UserID, []],
            CreatedBy: [note.CreatedBy, []],
            Date: [note.Date, []],
            Message: [note.Message, [Validators.required]],
            Type: [note.Type, []],
            TypeID: [note.TypeID, []],
            VerificationId: [note.VerificationId, []]
        });
    }

    generateDefaultNoteForm() {
        return this.formBuilder.group({
            NoteID: [null, []],
            UserID: [this.sessionService.user.UserID, []],
            CreatedBy: [null, []],
            Date: [null, []],
            Message: [null, [Validators.required]],
            Type: [this.typeOfNote, []],
            TypeID: [this.typeId, []],
            VerificationId: [null, []]
        });
    }

    deleteNote(note: CplNote) {
        this.noteService
            .deleteNote(note)
            .subscribe(() => {
                this.onSubmissionComplete.emit(true);
                this.toastr.success('Note successfully deleted.');

                const newPage = (this.notes?.Results?.length ?? 1) <= 1 ? (this.currentPage - 1) : this.currentPage;
                this.refreshNotesList(newPage);
            });
    }

    getNoteVisibilityText(note:CplNote){
        if(note.Visibility==0) return note.VerificationId && note.VerificationId != '' ? 'Note to Approver' : 'Note to Supervisors'
        else if(note.Visibility==1) return 'Note to User';
    }

    wasNoteSentInEmail(note:CplNote) {
        return note.Visibility == 0 && note.VerificationId || note.Visibility == 1;
    }

    addNote({ value, valid }: { value: CplNote, valid: boolean}) {
        if (valid && !this.busy) {
            this.busy = true;

            if (value.NoteID !== null) {
                this.noteService
                    .updateNote(value)
                    .pipe(
                        finalize( () => this.busy = false)
                    )
                    .subscribe(() => {
                        this.toastr.success('Note successfully updated.');

                        this.onSubmissionComplete.emit(true);
                        this.cancelAddNote();
                        this.refreshNotesList(FIRST_PAGE);
                    });
            }
            else {
                this.noteService
                    .createNote(value)
                    .pipe(
                        finalize( () => this.busy = false)
                    )
                    .subscribe(() => {
                        this.toastr.success('Note successfully created.');
                        this.onSubmissionComplete.emit(true);
                        this.cancelAddNote();
                        this.refreshNotesList(FIRST_PAGE);
                    });
            }
        }
    }

    setPage(page: number) {
        this.currentPage = page;
        if (this.currentPage < 1) {
            this.currentPage = 1;
        }
    }

    cancelAddNote() {
        this.currentNote = null;
        this.addNoteForm = this.generateDefaultNoteForm();
        this.addNoteForm.markAsPristine();
        this.addNoteForm.markAsUntouched();
    }

    canModifyNote(note: CplNote) {
        return !this.isReadOnly &&
            !this.currentNote &&
            note.Visibility == 0 &&
            (note.UserID === this.user.UserID || AccessRoleHelper.isAdmin(this.user.AccessRoleID)) &&
            !this.wasNoteSentInEmail(note)
    }

    refreshNotesList(page: number) {
        this.setPage(page);
        this.onPageChange();
    }

    onPageChange() {
        const limit = this.notes?.Limit ?? 5;
        this.noteService
            .getNotes(this.typeOfNote, this.typeId, this.notesToInclude, (this.currentPage - 1) * limit, limit, 'Date', true, null)
            .subscribe((result: PagedResponse) => {
                this.notes = result;
            });
    }
}